import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HomePage,
  useHomePageBulkUpdateMutation,
  useWidgetsLazyQuery,
  Widget,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { Modals } from '@lib/enums/modals';
import toast from '@lib/components/Toast/Toast';
import prepareTabsForBulkUpdate from '../utils/prepareTabsForBulkUpdate';
import { LocalWidget, GridLayoutItem } from '../utils/widgetConverters';
import copyWidgetsFromTemplate from '../utils/copyWidgetsFromTemplate';

interface UseActionsProps {
  homePages: Pick<HomePage, 'id' | 'name' | 'position' | 'isDefault'>[];
  refetchHomePages: () => void;
  isViewOnly: boolean;
  activeHomePageId?: string;
  setActiveHomePageId: (id: string) => void;
  localWidgets: LocalWidget[];
  setLocalWidgets: React.Dispatch<React.SetStateAction<LocalWidget[]>>;
  setLocalGrid: React.Dispatch<React.SetStateAction<GridLayoutItem[]>>;
  /** Function to close a modal by name */
  closeModal: (name: string) => void;
}

interface UseActionsReturn {
  // Apply template action
  handleApplyGeneralTemplate: () => Promise<void>;
  applyTemplateLoading: boolean;

  // Tab handlers
  handleSubmitCustomizeTabs: (values: Values) => Promise<void>;

  // Loading and error states
  bulkUpdateLoading: boolean;
  bulkUpdateError: ReturnType<typeof useHomePageBulkUpdateMutation>[1]['error'];
}

export default function useActions({
  homePages,
  refetchHomePages,
  isViewOnly,
  activeHomePageId,
  setActiveHomePageId,
  localWidgets,
  setLocalWidgets,
  setLocalGrid,
  closeModal,
}: UseActionsProps): UseActionsReturn {
  const { t } = useTranslation();

  // Mutations
  const [
    homePageBulkUpdate,
    {
      loading: bulkUpdateLoading,
      error: bulkUpdateError,
      reset: resetBulkUpdate,
    },
  ] = useHomePageBulkUpdateMutation();

  // Lazy query for fetching default page widgets
  const [fetchDefaultWidgets, { loading: applyTemplateLoading }] =
    useWidgetsLazyQuery();

  const handleApplyGeneralTemplate = useCallback(async () => {
    // Find the default home page
    const defaultPage = homePages.find((page) => page.isDefault);
    if (!defaultPage) {
      toast({ content: t('home.apply-template-no-default-page-error') });
      closeModal(Modals.HomeApplyTemplateModal);
      return;
    }

    // Fetch widgets from the default page
    const result = await fetchDefaultWidgets({
      variables: { homePageId: defaultPage.id },
    });

    // Check for query errors
    if (result.error) {
      toast({ content: t('home.apply-template-error') });
      closeModal(Modals.HomeApplyTemplateModal);
      return;
    }

    const defaultWidgets = (result.data?.data as Widget[]) || [];

    // Copy widgets with new tempIds and transformed attachments
    const { localWidgets: newLocalWidgets, localGrid: newLocalGrid } =
      copyWidgetsFromTemplate(defaultWidgets);

    // Mark existing widgets (with real IDs) as destroyed
    // These need to be deleted from the database when saving
    const destroyedWidgets: LocalWidget[] = localWidgets
      .filter((widget) => widget.id && !widget.isDestroyed)
      .map((widget) => ({
        ...widget,
        isDestroyed: true,
      }));

    // Combine destroyed widgets (for deletion) + new widgets (for creation)
    setLocalWidgets([...destroyedWidgets, ...newLocalWidgets]);
    setLocalGrid(newLocalGrid);

    closeModal(Modals.HomeApplyTemplateModal);
  }, [
    homePages,
    fetchDefaultWidgets,
    localWidgets,
    setLocalWidgets,
    setLocalGrid,
    closeModal,
    t,
  ]);

  // Customize tabs submit handler
  const handleSubmitCustomizeTabs = useCallback(
    async (values: Values) => {
      const changes = prepareTabsForBulkUpdate(values, homePages, {
        isViewOnly,
      });

      if (!changes || changes.length === 0) {
        closeModal(Modals.HomeCustomizeTabsModal);
        resetBulkUpdate();
        return;
      }

      // Check if current tab is being deleted
      // eslint-disable-next-line no-underscore-dangle
      const isCurrentTabDeleted = changes.some(
        (change) =>
          '_destroy' in change &&
          // eslint-disable-next-line no-underscore-dangle
          change._destroy === true &&
          change.id === activeHomePageId,
      );

      try {
        await homePageBulkUpdate({
          variables: {
            attributes: changes,
          },
        });
        closeModal(Modals.HomeCustomizeTabsModal);
        resetBulkUpdate();
        refetchHomePages();

        // If current tab was deleted, switch to default tab
        if (isCurrentTabDeleted) {
          const defaultPage = homePages.find((page) => page.isDefault);
          const firstPage = homePages.find(
            (page) =>
              !changes.some(
                (change) =>
                  '_destroy' in change &&
                  // eslint-disable-next-line no-underscore-dangle
                  change._destroy === true &&
                  change.id === page.id,
              ),
          );
          const newActiveId = defaultPage?.id || firstPage?.id;
          if (newActiveId) {
            setActiveHomePageId(newActiveId);
          }
        }
      } catch {
        // Show user-visible error toast for any failures
        toast({ content: t('home.update-tabs-error') });
      }
    },
    [
      homePages,
      homePageBulkUpdate,
      refetchHomePages,
      closeModal,
      resetBulkUpdate,
      activeHomePageId,
      setActiveHomePageId,
      isViewOnly,
      t,
    ],
  );

  return {
    // Apply template action
    handleApplyGeneralTemplate,
    applyTemplateLoading,

    // Tab handlers
    handleSubmitCustomizeTabs,

    // Loading and error states
    bulkUpdateLoading,
    bulkUpdateError,
  };
}
